import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Scroll reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale, .stagger-children').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-contact-message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'envoi du message');
      }

      setSubmitStatus('success');
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        sujet: '',
        message: ''
      });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Erreur:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
      setTimeout(() => {
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-clinic-blue to-clinic-blue-dark text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute top-8 left-8 w-20 h-20 bg-white/5 rounded-full animate-float hidden lg:block"></div>
        <div className="absolute bottom-12 right-16 w-14 h-14 bg-white/5 rounded-full animate-float hidden lg:block" style={{ animationDelay: '2s' }}></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="hero-title text-3xl md:text-5xl font-bold mb-4 md:mb-6">
              Contactez-nous
            </h1>
            <p className="hero-subtitle text-base md:text-xl text-white/90 leading-relaxed">
              Notre équipe est à votre écoute pour répondre à toutes vos questions et vous accompagner dans vos démarches de santé
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-8 sm:py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            
            {/* Contact Information */}
            <div className="space-y-6 md:space-y-8 reveal-left">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-clinic-blue-dark mb-4 md:mb-6">
                  Nos coordonnées
                </h2>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  N'hésitez pas à nous contacter par téléphone, email ou à venir nous rendre visite directement à la clinique. Nous sommes là pour vous aider.
                </p>
              </div>

              <div className="space-y-4 md:space-y-6 stagger-children">
                {/* Téléphone */}
                <div className="flex items-start gap-4 p-4 md:p-6 bg-clinic-blue/5 rounded-2xl hover:bg-clinic-blue/10 transition-all duration-300 card-lift">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl md:text-3xl text-clinic-green icon-bounce"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base md:text-lg text-clinic-blue-dark mb-2">Téléphone</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-1">+229 01 95 40 58 91</p>
                    <p className="text-sm md:text-base text-gray-600">+229 01 94 27 54 24</p>
                    <p className="text-xs md:text-sm text-gray-500 mt-2">Disponible 24h/24, 7j/7</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4 p-4 md:p-6 bg-clinic-blue/5 rounded-2xl hover:bg-clinic-blue/10 transition-all duration-300 card-lift">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl md:text-3xl text-clinic-green icon-bounce"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base md:text-lg text-clinic-blue-dark mb-2">Email</h3>
                    <p className="text-sm md:text-base text-gray-600">clinicfam@yahoo.com</p>
                    <p className="text-xs md:text-sm text-gray-500 mt-2">Réponse sous 24h</p>
                  </div>
                </div>

                {/* Adresse */}
                <div className="flex items-start gap-4 p-4 md:p-6 bg-clinic-blue/5 rounded-2xl hover:bg-clinic-blue/10 transition-all duration-300 card-lift">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl md:text-3xl text-clinic-green icon-bounce"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base md:text-lg text-clinic-blue-dark mb-2">Adresse</h3>
                    <p className="text-sm md:text-base text-gray-600">99QX+7W, Cotonou Bénin</p>
                    <p className="text-sm md:text-base text-gray-600">04 BP 127 lot 1862 Fifadji</p>
                  </div>
                </div>

                {/* Horaires */}
                <div className="flex items-start gap-4 p-4 md:p-6 bg-clinic-blue/5 rounded-2xl hover:bg-clinic-blue/10 transition-all duration-300 card-lift">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-clinic-green/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl md:text-3xl text-clinic-green icon-bounce"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-base md:text-lg text-clinic-blue-dark mb-2">Horaires d'ouverture</h3>
                    <p className="text-sm md:text-base text-gray-600">Urgences : 24h/24, 7j/7</p>
                    <p className="text-sm md:text-base text-gray-600">Consultations : Lun-Dim 8h-18h</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-2xl overflow-hidden shadow-lg h-64 md:h-80 reveal">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.7234567890123!2d2.449!3d6.388!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s99QX%2B7W%20Cotonou%2C%20B%C3%A9nin!5e0!3m2!1sfr!2sbj!4v1234567890123!5m2!1sfr!2sbj"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localisation Clinique Sainte Famille"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-[var(--shadow-elevated)] reveal-right">
              <h2 className="text-2xl md:text-3xl font-bold text-clinic-blue-dark mb-4 md:mb-6">
                Envoyez-nous un message
              </h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-clinic-green/10 border-2 border-clinic-green/30 rounded-xl flex items-start gap-3 animate-fadeIn">
                  <i className="ri-checkbox-circle-line text-2xl text-clinic-green flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-clinic-green">Message envoyé avec succès !</p>
                    <p className="text-sm text-gray-600 mt-1">Nous vous répondrons dans les plus brefs délais.</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3 animate-fadeIn">
                  <i className="ri-error-warning-line text-2xl text-red-500 flex-shrink-0"></i>
                  <div>
                    <p className="font-semibold text-red-500">Erreur lors de l'envoi</p>
                    <p className="text-sm text-gray-600 mt-1">{errorMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="reveal" style={{ transitionDelay: '0.05s' }}>
                    <label htmlFor="nom" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="nom"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 text-sm hover:border-gray-300"
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className="reveal" style={{ transitionDelay: '0.1s' }}>
                    <label htmlFor="prenom" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="prenom"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 text-sm hover:border-gray-300"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div className="reveal" style={{ transitionDelay: '0.15s' }}>
                  <label htmlFor="email" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 text-sm hover:border-gray-300"
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div className="reveal" style={{ transitionDelay: '0.2s' }}>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 text-sm hover:border-gray-300"
                    placeholder="+229 XX XX XX XX"
                  />
                </div>

                <div className="reveal" style={{ transitionDelay: '0.25s' }}>
                  <label htmlFor="sujet" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                    Sujet <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="sujet"
                    name="sujet"
                    value={formData.sujet}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 text-sm pr-8 cursor-pointer hover:border-gray-300"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="rendez-vous">Prise de rendez-vous</option>
                    <option value="information">Demande d'information</option>
                    <option value="urgence">Urgence médicale</option>
                    <option value="reclamation">Réclamation</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div className="reveal" style={{ transitionDelay: '0.3s' }}>
                  <label htmlFor="message" className="block text-sm font-semibold text-clinic-blue-dark mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-clinic-green focus:outline-none transition-all duration-300 resize-none text-sm hover:border-gray-300"
                    placeholder="Décrivez votre demande en détail..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">
                    {formData.message.length}/500 caractères
                  </p>
                </div>

                <div className="reveal" style={{ transitionDelay: '0.35s' }}>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-ripple w-full px-8 py-4 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer le message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-8 sm:py-12 md:py-20 bg-gradient-to-b from-white to-clinic-blue/5">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-clinic-blue-dark mb-8 md:mb-12 text-center reveal">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4 stagger-children">
            <details className="bg-white p-6 rounded-2xl border border-gray-100 card-elevated group cursor-pointer">
              <summary className="font-semibold text-clinic-blue-dark cursor-pointer list-none flex items-center justify-between">
                <span>Comment prendre rendez-vous ?</span>
                <i className="ri-arrow-down-s-line text-2xl text-clinic-green group-open:rotate-180 transition-transform duration-300"></i>
              </summary>
              <p className="text-sm md:text-base text-gray-600 mt-4 leading-relaxed">
                Vous pouvez prendre rendez-vous en ligne via notre plateforme, par téléphone au +229 01 95 40 58 91, ou directement à l'accueil de la clinique.
              </p>
            </details>

            <details className="bg-white p-6 rounded-2xl border border-gray-100 card-elevated group cursor-pointer">
              <summary className="font-semibold text-clinic-blue-dark cursor-pointer list-none flex items-center justify-between">
                <span>Quels sont les moyens de paiement acceptés ?</span>
                <i className="ri-arrow-down-s-line text-2xl text-clinic-green group-open:rotate-180 transition-transform duration-300"></i>
              </summary>
              <p className="text-sm md:text-base text-gray-600 mt-4 leading-relaxed">
                Nous acceptons les paiements en espèces, par carte bancaire, et par mobile money. Les assurances santé sont également acceptées.
              </p>
            </details>

            <details className="bg-white p-6 rounded-2xl border border-gray-100 card-elevated group cursor-pointer">
              <summary className="font-semibold text-clinic-blue-dark cursor-pointer list-none flex items-center justify-between">
                <span>Y a-t-il un service d'urgence ?</span>
                <i className="ri-arrow-down-s-line text-2xl text-clinic-green group-open:rotate-180 transition-transform duration-300"></i>
              </summary>
              <p className="text-sm md:text-base text-gray-600 mt-4 leading-relaxed">
                Oui, notre service d'urgence est disponible 24h/24 et 7j/7. En cas d'urgence, appelez directement le +229 01 95 40 58 91.
              </p>
            </details>

            <details className="bg-white p-6 rounded-2xl border border-gray-100 card-elevated group cursor-pointer">
              <summary className="font-semibold text-clinic-blue-dark cursor-pointer list-none flex items-center justify-between">
                <span>Puis-je obtenir mes résultats d'analyses en ligne ?</span>
                <i className="ri-arrow-down-s-line text-2xl text-clinic-green group-open:rotate-180 transition-transform duration-300"></i>
              </summary>
              <p className="text-sm md:text-base text-gray-600 mt-4 leading-relaxed">
                Oui, une fois vos analyses terminées, vous pouvez consulter vos résultats en ligne via votre espace patient sécurisé.
              </p>
            </details>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}